import type {VoidFunctionComponent} from "react";
import React from "react";
import s from "./InfoButtonDocumentation.module.scss";
import {Example} from "../Example";
import {ComponentDescription} from "../documentationTools/ComponentDescription";
import {Property} from "../documentationTools/Property";
import {PropertyListHeader} from "../documentationTools/PropertyListHeader";
import {IPropertyWrapper} from "../documentationTools/DocumentationTypes";
import {sortProperties} from "../documentationTools/documentation-utility";
import {getLoremIpsumText} from "../../../util/string-util";
import InfoButton from "../../../components/common/infoButton/InfoButton";
import {iconValues} from "../../../components/common/FontIcon";

export interface IContainerDocumentationProps {
}

const sampleTextSmall = getLoremIpsumText(100);
const sampleTextLarge = getLoremIpsumText(2000);


export const InfoButtonDocumentation: VoidFunctionComponent<IContainerDocumentationProps> = ({}) => {
  let propertyIndex = 0;

  const properties: IPropertyWrapper[] = [
    {
      isRequired: false,
      name: "ariaLabel",
      element: <Property
        key={++propertyIndex}
        name={"ariaLabel"}
        description={"Causes an aria label to be associated with the help icon."}
        isRequired={false}
        type={"string"}
        examples={<>
          <Example title={"ariaLabel = 'Info button'"}>
            <InfoButton
              ariaLabel={"Info button"}
              text={sampleTextSmall}
            />
          </Example>
        </>}
      />
    },

    {
      isRequired: false,
      name: "persistState",
      element: <Property
        key={++propertyIndex}
        name={"persistState"}
        description={"Determines whether or not the state of the InfoButton is persisted to localStorage. State is persisted as a boolean value where true = open and false = closed. If persistState is true, storageKeyId must also be provided."}
        isRequired={false}
        type={"boolean"}
        defaultValue={"false"}
        examples={<>
          <Example title={"persistState = true"}>
            <InfoButton
              persistState={true}
              storageKeyId={"isPersistStateExampleOpen"}
              text={sampleTextSmall}
            />
          </Example>
        </>}
      />
    },

    {
      isRequired: false,
      name: "defaultState",
      element: <Property
        key={++propertyIndex}
        name={"defaultState"}
        description={"Determines if the help dialog is initially rendered open or closed. Setting is ignored if persistState is true and a value exists in localStorage for the provided storageKeyId."}
        isRequired={false}
        type={'"open" | "closed"'}
        defaultValue={'"closed"'}
        examples={<>
          <Example title={"defaultState = 'closed'"}>
            <InfoButton
              defaultState={"closed"}
              text={sampleTextSmall}
            />
          </Example>
        </>}
      />
    },

    {
      isRequired: true,
      name: "text",
      element: <Property
        key={++propertyIndex}
        name={"text"}
        description={"Text that will appear in the help information below the icon and title."}
        isRequired={true}
        type={'string | ReactNode'}
        examples={<>
          <Example title={"Example with text = a small string"}>
            <InfoButton
              text={sampleTextSmall}
            />
          </Example>
          <br />
          <Example title={"Example with text = a large string"}>
            <InfoButton
              text={sampleTextLarge}
            />
          </Example>
          <br />
          <Example title={"Example with text = a ReactNode"}>
            <InfoButton
              text={<img src={"favicon.png"} />}
            />
          </Example>
        </>}
      />
    },

    {
      isRequired: false,
      name: "title",
      element: <Property
        key={++propertyIndex}
        name={"title"}
        description={"Text that will appear in the help information below the icon and above the main text."}
        isRequired={false}
        type={'string'}
        examples={<>
          <Example title={"Example with title = My title"}>
            <InfoButton
              title={"My title"}
              text={sampleTextSmall}
            />
          </Example>
        </>}
      />
    },

    {
      isRequired: false,
      name: "icon",
      element: <Property
        key={++propertyIndex}
        name={"icon"}
        description={"An Icon or ReactNode that will appear in the help modal at the very top above the title and body text."}
        isRequired={false}
        type={'ReactNode | ' + '"' + iconValues.join('" | "') + '"'}
        examples={<>
          <Example title={"Example with icon = 'icon-songs'"}>
            <InfoButton
              icon={"icon-songs"}
              text={sampleTextSmall}
            />
          </Example>

          <Example title={"Example with icon = ReactNode"}>
            <InfoButton
              icon={<img src={"favicon.png"} />}
              text={sampleTextSmall}
            />
          </Example>
        </>}
      />
    },

    {
      isRequired: false,
      name: "additionalClassName",
      element: <Property
        key={++propertyIndex}
        name={"additionalClassName"}
        description={"Allows passing in additional css class names for custom styling of the icon section."}
        isRequired={false}
        type={'space separated css class(es)'}
        examples={<>
          <Example title={"Example with additionalClassName set"}>
            <InfoButton
              additionalClassName={"my-custom-class another-class"}
              text={sampleTextSmall}
            />
          </Example>
        </>}
      />
    },

    {
      isRequired: false,
      name: "storageKeyId",
      element: <Property
        key={++propertyIndex}
        name={"storageKeyId"}
        description={"Required if persistState is true. This is the storage key that will be used with localStorage to save the state of the InfoButton (i.e. open / closed). The storageKeyId can be anything, but it is recommended to use a string in the following format: 'is[FeatureName]InfoButtonOpen'."}
        isRequired={false}
        type={'string'}
        examples={<>
          <Example title={"Example with storageKeyId set"}>
            <InfoButton
              persistState={true}
              storageKeyId={"isStorageKeyIdExampleOpen"}
              text={sampleTextSmall}
            />
          </Example>
        </>}
      />
    },



  ];

  sortProperties(properties);

  return <div className={s.container}>
    <ComponentDescription
      name={"InfoButton"}
      description={"Renders a help icon that when clicked opens a popup modal with additional information."}
    />

    <PropertyListHeader properties={properties} />

    {properties.map((property, index) => {
      return <React.Fragment key={index}>
        {property.element}
        {index < properties.length - 1 && <hr className={s.propertyDivider} />}
      </React.Fragment>
    })}
  </div>;
}
